import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { Chart, Note, Patient, PilotForm } from './ehr-pilot.types';
export type { Note } from './ehr-pilot.types';

const call = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const response = await apiRequest(method, `/api/ehr-pilot${path}`, body);
  return response.json();
};

const decodeText = (content: Chart['documents'][number]['document']['content'] | undefined): string => {
  try {
    const data = content?.[0]?.attachment?.data;
    if (typeof data !== 'string') return 'Ekki tókst að lesa innihald skráningar.';
    return new TextDecoder().decode(Uint8Array.from(atob(data), (char) => char.charCodeAt(0)));
  } catch {
    return 'Ekki tókst að lesa innihald skráningar.';
  }
};

export const NoteStatus = ({ note }: { note: Note }) => {
  if (note.correction_pending && note.delivery_state === 'hold')
    return (
      <p role="alert" className="text-destructive">
        Afturköllun hefur stöðvast. Skráning gæti enn birst í sjúkraskrá. Hafðu samband við stuðning.
      </p>
    );
  if (note.terminal) return <output>Skráning hefur verið merkt röng og er falin í sjúkraskrá.</output>;
  if (note.delivery_state === 'hold')
    return (
      <p role="alert" className="flex items-center gap-2 text-destructive">
        <CircleAlert aria-hidden="true" /> Vistun stöðvuð. Ekki búa til aðra skráningu. Hafðu samband við stuðning.
      </p>
    );
  if (note.correction_pending)
    return (
      <output className="flex items-center gap-2">
        <Clock aria-hidden="true" /> Bíður staðfestingar á afturköllun. Skráning gæti enn birst í sjúkraskrá.
      </output>
    );
  if (note.ehr_document_id)
    return (
      <output className="flex items-center gap-2 text-green-700">
        <CheckCircle2 aria-hidden="true" /> Vistun í sjúkraskrá staðfest.
      </output>
    );
  if (note.attested)
    return (
      <output className="flex items-center gap-2">
        <Clock aria-hidden="true" /> Yfirferð staðfest. Bíður vistunar í sjúkraskrá.
      </output>
    );
  return <output>Drög vistuð. Eftir er að staðfesta yfirferð.</output>;
};

const Pilot = ({ scope }: { scope: string }) => {
  const queries = useQueryClient();
  const { register, handleSubmit, control, watch, setValue, reset } = useForm<PilotForm>({
    defaultValues: { identifier: '', text: '', reviewed: false },
  });
  const identifier = watch('identifier');
  const text = watch('text');
  const reviewed = watch('reviewed');
  const setIdentifier = (value: string) => setValue('identifier', value);
  const setReviewed = (value: boolean) => setValue('reviewed', value);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [saved, setSaved] = useState<Note | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const fragment = window.location.hash.slice(1);
  const fragmentParams = new URLSearchParams(fragment);
  const creationFromUrl = fragmentParams.get('creation') ?? '';
  const replacementFromUrl = fragmentParams.get('replaces') ?? '';
  const replaces = useRef(/^[0-9a-f-]{36}$/.test(replacementFromUrl) ? replacementFromUrl : undefined);
  const creation = useRef(/^[0-9a-f-]{36}$/.test(creationFromUrl) ? creationFromUrl : crypto.randomUUID());
  const correction = useRef(crypto.randomUUID());
  const clinicalTime = useRef(new Date().toISOString());
  const config = useQuery({
    queryKey: ['ehr-pilot-config', scope],
    queryFn: () => call<{ enabled: boolean; can_write?: boolean }>('GET', '/config'),
    retry: false,
  });
  const resumeId = creationFromUrl || fragment;
  const resumePath = creationFromUrl ? `/notes/by-creation/${resumeId}` : `/notes/${resumeId}`;
  const resume = useQuery({
    queryKey: ['ehr-pilot-resume', scope, resumePath],
    queryFn: () => call<Note>('GET', resumePath),
    enabled: Boolean(config.data && !saved && /^[0-9a-f-]{36}$/.test(resumeId)),
    retry: false,
  });
  useEffect(() => {
    if (resume.data && !saved) {
      const recovered = resume.data;
      setSaved(recovered);
      reset({ identifier: '', text: recovered.text, reviewed: false });
      clinicalTime.current = recovered.clinical_time;
      setPatient({ account_id: recovered.patient_account_id, org_id: recovered.org_id });
      queries.setQueryData(['ehr-pilot-note', scope, recovered.id], recovered);
    }
  }, [resume.data, saved, queries, scope, reset]);
  const statusKey = ['ehr-pilot-note', scope, saved?.id];
  const status = useQuery({
    queryKey: statusKey,
    queryFn: () => call<Note>('GET', `/notes/${saved?.id}`),
    enabled: Boolean(saved?.id),
    refetchInterval: (query) => {
      const current = query.state.data;
      return current?.attested &&
        !current.terminal &&
        current.delivery_state !== 'hold' &&
        (!current.ehr_document_id || current.correction_pending)
        ? 2000
        : false;
    },
  });
  const note = status.data ?? saved;
  const remember = async (value: Note) => {
    await queries.cancelQueries({ queryKey: ['ehr-pilot-note', scope, value.id] });
    setSaved(value);
    window.history.replaceState(null, '', `${window.location.pathname}#${value.id}`);
    queries.setQueryData(['ehr-pilot-note', scope, value.id], value);
    setReviewed(false);
  };
  const resolve = useMutation({
    mutationFn: (value: string) => call<Patient>('POST', '/patient', { identifier: value }),
    onSuccess: (value) => {
      setPatient(value);
      setReviewed(false);
    },
  });
  const refreshState = async () => {
    await queries.invalidateQueries({ queryKey: ['ehr-pilot-note', scope] });
    await queries.invalidateQueries({ queryKey: ['ehr-pilot-resume', scope] });
  };
  const save = useMutation({
    onMutate: () => {
      if (!saved)
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}#creation=${creation.current}${replaces.current ? `&replaces=${replaces.current}` : ''}`
        );
    },
    mutationFn: (values: PilotForm) =>
      call<Note>(saved ? 'PUT' : 'POST', saved ? `/notes/${saved.id}` : '/notes', {
        patient_account_id: patient?.account_id,
        text: values.text,
        clinical_time: clinicalTime.current,
        ...(saved
          ? { expected_revision: note?.revision }
          : { creation_key: creation.current, replaces_id: replaces.current }),
      }),
    onSuccess: remember,
    onSettled: refreshState,
  });
  const attest = useMutation({
    mutationFn: () =>
      call<Note>('POST', `/notes/${note?.id}/attest`, {
        revision: note?.revision,
        content_digest: note?.content_digest,
      }),
    onSuccess: remember,
    onSettled: refreshState,
  });
  const retract = useMutation({
    mutationFn: () =>
      call<Note>('POST', `/notes/${note?.id}/entered-in-error`, {
        operation_id: correction.current,
        reason_code: 'entered_in_error',
      }),
    onSuccess: remember,
    onSettled: refreshState,
  });
  const retryDelivery = useMutation({
    mutationFn: () =>
      call<Note>('POST', `/notes/${note?.id}/retry`, { expected_generation: note?.delivery_generation }),
    onSuccess: remember,
    onSettled: refreshState,
  });
  const canRecover = config.data?.can_write === true;
  const canWrite = canRecover && config.data?.enabled === true;
  const chart = useQuery({
    queryKey: [
      'ehr-pilot-chart',
      scope,
      patient?.account_id,
      note?.id,
      note?.ehr_document_id,
      note?.correction_pending,
      note?.terminal,
    ],
    queryFn: () => call<Chart>('POST', '/documents/search', { patient_account_id: patient?.account_id }),
    enabled: chartOpen && Boolean(patient),
  });
  const busy =
    resume.isFetching ||
    resolve.isPending ||
    save.isPending ||
    attest.isPending ||
    retract.isPending ||
    retryDelivery.isPending;
  const uncertain =
    status.isError ||
    [save, attest, retract, retryDelivery].some(
      (mutation) => mutation.isError && status.dataUpdatedAt <= mutation.submittedAt
    );
  const error = resolve.isError || chart.isError || (resume.isError && !saved) || uncertain;
  const dirty = note?.text !== text || note?.patient_account_id !== patient?.account_id;
  if (config.isPending) return <output>Hleð prófun…</output>;
  if (config.isError || (!config.data?.enabled && !resumeId && !saved))
    return <output>Prófun er ekki virk fyrir þennan aðgang.</output>;
  return (
    <main data-sentry-mask className="ph-no-capture mx-auto max-w-3xl space-y-6 p-6">
      <h1 className="font-semibold text-2xl">Prófun á sameiginlegri sjúkraskrá</h1>
      <p>Aðeins tilbúnir sjúklingar og prófunartexti. Ekki skrá raunverulegar heilsufarsupplýsingar.</p>
      <section className="space-y-3">
        <Label htmlFor="pilot-patient">Kennitala prófunarsjúklings</Label>
        <Input
          id="pilot-patient"
          disabled={busy || note?.attested || !canWrite}
          {...register('identifier', {
            onChange: () => {
              setPatient(null);
              setReviewed(false);
              setChartOpen(false);
            },
          })}
        />
        <Button
          disabled={busy || !identifier || note?.attested || !canWrite}
          onClick={handleSubmit(({ identifier }) => resolve.mutate(identifier))}
        >
          Velja prófunarsjúkling
        </Button>
        {patient && (
          <output>Prófunarsjúklingur valinn{identifier ? `: ${identifier}` : ' í varðveittri skráningu'}</output>
        )}
      </section>
      <section className="space-y-3">
        <Label htmlFor="pilot-note">Prófunarskráning</Label>
        <Textarea
          id="pilot-note"
          disabled={busy || note?.attested || !canWrite}
          {...register('text', { onChange: () => setReviewed(false) })}
        />
        <Button
          disabled={busy || !patient || !text.trim() || note?.attested || !canWrite}
          onClick={handleSubmit((values) => save.mutate(values))}
        >
          Vista drög
        </Button>
        {note && (
          <>
            <h2 className="font-semibold">Vistuð drög til yfirferðar</h2>
            <pre className="whitespace-pre-wrap rounded border p-4">{note.text}</pre>
            {!note.attested && (
              <div className="flex items-center gap-2">
                <Controller
                  name="reviewed"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="pilot-reviewed"
                      checked={field.value}
                      disabled={dirty || busy || !canWrite}
                      onCheckedChange={(value) => field.onChange(value === true)}
                    />
                  )}
                />
                <Label htmlFor="pilot-reviewed">Ég hef yfirfarið textann og valinn sjúkling.</Label>
              </div>
            )}
            <Button
              disabled={busy || !reviewed || dirty || note.attested || !canWrite}
              onClick={handleSubmit(() => attest.mutate())}
            >
              Staðfesta yfirferð og vista í sjúkraskrá
            </Button>
            {!uncertain && <NoteStatus note={note} />}
            {note.delivery_state === 'hold' && note.delivery_error && (
              <output>
                {note.delivery_error === 'retry_exhausted'
                  ? 'Sjálfvirkar tilraunir hafa stöðvast eftir endurteknar villur.'
                  : note.delivery_error === 'receipt_conflict'
                    ? 'Ósamræmi fannst í staðfestingu. Stuðningur þarf að yfirfara stöðuna.'
                    : 'Sending stöðvaðist. Athugaðu tengingu og stöðu áður en þú reynir aftur.'}
              </output>
            )}
            {note.can_retry_delivery && (
              <Button disabled={busy || !canRecover} onClick={() => retryDelivery.mutate()}>
                Reyna sendingu aftur
              </Button>
            )}
            {note.terminal && !note.correction_pending && note.delivery_state !== 'hold' && (
              <Button
                variant="outline"
                disabled={busy || !canWrite}
                onClick={() => {
                  replaces.current = note.id;
                  creation.current = crypto.randomUUID();
                  correction.current = crypto.randomUUID();
                  clinicalTime.current = new Date().toISOString();
                  window.history.replaceState(null, '', `${window.location.pathname}#replaces=${note.id}`);
                  setSaved(null);
                  setPatient(null);
                  setIdentifier('');
                  setValue('text', '');
                  setReviewed(false);
                  save.reset();
                  attest.reset();
                  retract.reset();
                  setChartOpen(false);
                }}
              >
                Ný prófunarskráning í stað þeirrar röngu
              </Button>
            )}
            {note.attested && !note.terminal && !note.correction_pending && (
              <Button variant="destructive" disabled={busy || !canRecover} onClick={() => retract.mutate()}>
                Merkja skráningu ranga og fela í sjúkraskrá
              </Button>
            )}
          </>
        )}
      </section>
      <section className="space-y-3">
        <h2 className="font-semibold">Skráningar úr sjúkraskrá (allt að 20)</h2>
        {chart.isFetching && <output>Sæki skráningar úr sjúkraskrá…</output>}
        <Button
          variant="outline"
          disabled={!patient || chart.isFetching}
          onClick={() => {
            if (chartOpen) void chart.refetch();
            else setChartOpen(true);
          }}
        >
          Lesa skráningar úr sjúkraskrá
        </Button>
        {chart.data?.documents.map((item) => (
          <article key={item.document.id} className="rounded border p-4">
            <pre className="whitespace-pre-wrap">{decodeText(item.document?.content)}</pre>
          </article>
        ))}
        {chart.isSuccess && chart.data.documents.length === 0 && <p>Engar virkar skráningar fundust.</p>}
      </section>
      {error && (
        <p role="alert" className="text-destructive">
          Aðgerð tókst ekki. Athugaðu stöðu áður en þú reynir aftur. Ekki búa til aðra skráningu.
        </p>
      )}
    </main>
  );
};

const EhrPilot = () => {
  const { user } = useAuth();
  const scope = `${user?.authOrgId}:${user?.authAccountId}`;
  return <Pilot key={scope} scope={scope} />;
};

export default EhrPilot;
